import 'simplebar-react/dist/simplebar.min.css'
import { routes } from './routes'
import { Toaster } from 'react-hot-toast'
import { useEffect } from 'react'
import { config } from '@config'
import Maintenance from './Maintenance'
import { useSelector } from 'react-redux'
import { MsalProvider } from '@azure/msal-react'
import { Helmet } from 'react-helmet-async'
import { ThemeInitializer } from '@theme/theme-initializer'
import { parseScripts } from '@utils/util'
import { getIntercomScript } from '@utils/intercom'
import CryptoJS from 'crypto-js'
import { useSearchParams } from 'react-router-dom'
import { setAuthToken } from '@services/auth-service'
import { useUser } from '@hooks/user'

export const App = ({ instance }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const user = useUser()
  const { theme, seo: seoData } = useSelector((state) => state.partnerDetails)

  const maintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true'
  const isDifferentDomain = window.location.origin !== process.env.REACT_APP_FRONTEND_BASE_URL
  const shouldShowIntercom = !(maintenanceMode || user?.partnerAccountId || (!user?.isPartner && isDifferentDomain))

  useEffect(() => {
    const authToken = searchParams.get('auth_token')
    if (authToken) {
      setAuthToken(authToken)
      searchParams.delete('auth_token')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (!user || !shouldShowIntercom) {
      if (window.Intercom) {
        window.Intercom('shutdown')
      }
      return
    }

    const secretKey = process.env.REACT_APP_SECRET_KEY
    const hash = CryptoJS.HmacSHA256(user?._id?.toString(), secretKey).toString(CryptoJS.enc.Hex)
    const intercomSettings = {
      app_id: config.INTERCOM_APP_ID,
      user_id: user?._id?.toString(),
      user_hash: hash,
      email: user?.email
    }

    const loadIntercomScript = () => {
      if (document.getElementById('intercom-script')) return;
  
      const script = document.createElement('script');
      script.id = 'intercom-script';
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://widget.intercom.io/widget/${config.INTERCOM_APP_ID}`;
      script.onload = () => {
        window.Intercom('boot', intercomSettings);
      };
      document.body.appendChild(script);
    };


    if (window.Intercom && typeof window.Intercom === 'function') {
      window.Intercom('shutdown');
      window.Intercom('boot', intercomSettings);
    } else {
      window.intercomSettings = intercomSettings;
      loadIntercomScript();
    }
  }, [user?._id, user?.email, shouldShowIntercom])

  useEffect(() => {
    if (!user?.email) {
      return
    }

    if (window.clarity) {
      window.clarity('set', 'userEmail', user.email)
      return
    }

    const clarityCheck = setInterval(() => {
      if (window.clarity) {
        window.clarity('set', 'userEmail', user.email)
        clearInterval(clarityCheck)
      }
    }, 1000)

    return () => clearInterval(clarityCheck)
  }, [user?.email])

  if (maintenanceMode) {
    return <Maintenance lead={false} />
  }

  return (
    <MsalProvider instance={instance}>
      <Helmet>
        <title>{seoData?.title}</title>
        <meta name='description' content={seoData?.description} />
        <meta property='og:title' content={seoData?.title} />
        <meta property='og:description' content={seoData?.description} />
        {/* Favicon  */}
        <link rel='apple-touch-icon' sizes='180x180' href={theme?.favicon || theme?.logo || '/apple-touch-icon.png'} />
        <link rel='icon' href={theme?.favicon || theme?.logo || '/main-monogram.ico'} />
        <link rel='icon' type='image/png' sizes='32x32' href={theme?.favicon || theme?.logo || '/MainFavIcon.png'} />
        <link rel='icon' type='image/png' sizes='16x16' href={theme?.favicon || theme?.logo || '/mainmonogram.png'} />
        {!user?.isPartner && parseScripts(theme?.scriptContent)?.map((script, index) => (
          <script key={index} {...script} />
        ))}
      </Helmet>
      <ThemeInitializer>

        {routes}
        <Toaster
          toastOptions={{
            duration: 2000,
            success: {
              style: {
                background: theme.colors?.main,
                color: theme.colors?.contrastText
              },
              iconTheme: {
                primary: theme.colors?.contrastText,
                secondary: theme.colors?.main
              }
            },
            error: {
              style: {
                background: 'red',
                color: theme.colors?.contrastText
              },
              iconTheme: {
                primary: theme.colors?.contrastText,
                secondary: 'red'
              }
            },
            loading: {
              style: {
                background: theme.colors?.contrastText,
                color: theme.colors?.main
              },
              iconTheme: {
                primary: theme.colors?.main,
                secondary: theme.colors?.contrastText
              }
            }
          }}
        />
      </ThemeInitializer>
    </MsalProvider>
  )
}
